import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'components/DesignSystem/Icon';
import useOverviewKpis from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewKpis';
import useOverviewLocations from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewLocations';
import useOverviewMap from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewMap';
import useOverviewMapVariables from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewMapVariables';
import useOverviewMapError from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewMapError';
import { AgroOverviewLocation } from 'types/Meteorology/Agro/Overview/Dashboard';
import { selectAgroOverviewSelectedVariable } from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import { cssVar } from 'constants/theme';
import SummaryCardsMobile from './SummaryCards';
import LocationsList from './LocationsList';
import LocationDetailSheet from './LocationDetailSheet';
import MapCard from './MapCard';
import VariableSheet from './MapCard/VariableSheet';
import LayersSheet from './MapCard/LayersSheet';
import FullscreenMap from './MapCard/FullscreenMap';
import ExportSheet from './ExportSheet';
import InfoSheet from './InfoSheet';
import PartErrorState from '../PartErrorState';

import './index.scss';

export default function DashboardMobile() {
  const selectedVariable = useSelector(selectAgroOverviewSelectedVariable);
  const kpis = useOverviewKpis();
  const table = useOverviewLocations();
  const map = useOverviewMap(selectedVariable);
  const mapVariablesPart = useOverviewMapVariables();
  const mapError = useOverviewMapError(map, mapVariablesPart);
  const { summary, isFetching: summaryFetching } = kpis;
  const { locations, total, isFetching: locationsFetching } = table;
  const { mapData, isFetching: mapFetching } = map;
  const { variables: mapVariables } = mapVariablesPart;

  const [infoOpen, setInfoOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [variableOpen, setVariableOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [detailLocation, setDetailLocation] = useState<AgroOverviewLocation | null>(null);

  // Estado vazio (§4 do contrato): total === 0 (undefined enquanto carrega
  // → NÃO é estado vazio). Predicado idêntico ao desktop.
  const isEmptyState = total === 0;
  const safeLocations = locations ?? [];

  return (
    <div className="agro-overview-mobile">
      <div className="agro-overview-mobile-header">
        <span className="agro-overview-mobile-subtitle">Condições agrometeorológicas</span>
        <div className="agro-overview-mobile-header-actions">
          <button
            type="button"
            aria-label="Como interpretar"
            className="agro-overview-mobile-icon-btn"
            onClick={() => setInfoOpen(true)}
          >
            <Icon iconType="info" color={cssVar('text-strong')} size="small" />
          </button>
          <button
            type="button"
            className="agro-overview-mobile-export-btn"
            onClick={() => setExportOpen(true)}
          >
            <Icon iconType="download" color={cssVar('text-strong')} size="small" />
            Exportar
          </button>
        </div>
      </div>

      {/* #2009 item 2: cada parte mostra a própria falha; as outras continuam. */}
      {kpis.errorKind ? (
        <PartErrorState
          label="Indicadores"
          subject="os indicadores"
          kind={kpis.errorKind}
          onRetry={kpis.retry}
        />
      ) : (
        <SummaryCardsMobile
          summary={summary}
          locations={safeLocations}
          loading={summaryFetching && !summary}
          isEmptyState={isEmptyState}
        />
      )}

      <MapCard
        locations={safeLocations}
        valuePoints={mapData?.pontos ?? []}
        variables={mapVariables ?? []}
        period={mapData ? { inicio: mapData.dataInicio, fim: mapData.dataFim } : null}
        loading={mapFetching}
        error={mapError.errorKind ? (
          <PartErrorState
            label="Mapa"
            subject="o mapa"
            kind={mapError.errorKind}
            onRetry={mapError.retry}
          />
        ) : undefined}
        onOpenVariable={() => setVariableOpen(true)}
        onOpenLayers={() => setLayersOpen(true)}
        onOpenFullscreen={() => setFullscreenOpen(true)}
        onSelectLocation={setDetailLocation}
      />

      {table.errorKind ? (
        <PartErrorState
          label="Localidades monitoradas"
          subject="as localidades"
          kind={table.errorKind}
          onRetry={table.retry}
        />
      ) : (
        <LocationsList
          locations={safeLocations}
          total={total}
          loading={locationsFetching && !locations}
          onSelect={setDetailLocation}
        />
      )}

      <LocationDetailSheet
        location={detailLocation}
        dismiss={() => setDetailLocation(null)}
      />
      <VariableSheet
        open={variableOpen}
        dismiss={() => setVariableOpen(false)}
        variables={mapVariables ?? []}
      />
      <LayersSheet open={layersOpen} dismiss={() => setLayersOpen(false)} />
      <FullscreenMap
        open={fullscreenOpen}
        dismiss={() => setFullscreenOpen(false)}
        locations={safeLocations}
        valuePoints={mapData?.pontos ?? []}
        variables={mapVariables ?? []}
      />
      <ExportSheet
        open={exportOpen}
        dismiss={() => setExportOpen(false)}
        locations={safeLocations}
      />
      <InfoSheet open={infoOpen} dismiss={() => setInfoOpen(false)} />
    </div>
  );
}
